import { useState } from 'react';
import type { EnlaceRrpp, EventoPanel, ListaDeInvitados } from '../../api/ops/portalRrpp';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { avisoBreveDeError } from '../../logica/estados';
import { enlaceDeReferencia } from '../../logica/navegacion';
import { useCabecera, type AccionCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { Apuntar } from './Apuntar';
import { copiar, fechaEvento, opsDelPortal, useCarga, type OpsPortal } from './carga';
import { Ganancias } from './Ganancias';
import { Invitados } from './Invitados';
import { PERMISOS_PORTAL, SECCIONES, SUBTITULOS, seccionDe, sinProtocolo, type Seccion } from './logica';
import '../../componentes/apartado.css';
import './portal-rrpp.css';

/**
 * **Portal del RRPP** (10.12): lo pinta la pantalla «Listas y RRPP» cuando `listas.listas.ver` llega con el ámbito «lo
 * suyo». Móvil primero: Inicio · Eventos · Invitados · Ventas · Comisiones (con Comisiones · Liquidaciones) en
 * `?seccion=`; `?invitado=<id>` abre su ficha (el «Ver» del aviso `listas.pase_no_entregado`, G791). La API ya filtra
 * «lo suyo»: el portal no mira el rol, solo los permisos efectivos y lo que responde.
 */

export interface DatosPortal {
    ops: OpsPortal;
    eventos: EventoPanel[];
    listas: Readonly<Record<string, ListaDeInvitados[]>>;
    enlaces: EnlaceRrpp[];
    recargarEnlaces: () => void;
    nombreEvento: (id: string) => string;
    nombreLista: (id: string) => string;
}

export function PortalRrpp(props: PropsPantalla) {
    const { negocio, busqueda, ruta, navegar, movil, soloLectura, membresia, nombreNegocio, yo } = props;
    const ops = opsDelPortal(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const seccion = seccionDe(busqueda);
    const [apuntar, setApuntar] = useState<{ evento: string | null } | null>(null);

    const eventos = useCarga(`eventos|${negocio}`, () => ops.listEvents(negocio, { status: ['published'], from: new Date(Date.now() - 12 * 3_600_000).toISOString(), limit: 20 }));
    const listaEventos = eventos.datos?.data ?? [];
    const idsEventos = listaEventos.map((e) => e.id).join(',');
    const listas = useCarga(eventos.datos === null ? null : `listas|${negocio}|${idsEventos}`, async () => {
        const pares = await Promise.all(listaEventos.map(async (e) => [e.id, (await ops.listGuestLists(negocio, e.id).catch(() => ({ data: [] }))).data] as const));
        const mapa: Record<string, ListaDeInvitados[]> = Object.fromEntries(pares);
        return mapa;
    });
    const enlaces = useCarga(permisos.puede(PERMISOS_PORTAL.enlaces) ? `enlaces|${negocio}` : null, () => ops.listPromoterLinks(negocio, { limit: 50 }));

    const puedeApuntar = permisos.puede(PERMISOS_PORTAL.apuntar);
    const acciones: AccionCabecera[] = puedeApuntar ? [{ clave: 'apuntar', texto: 'Apuntar a un invitado', icono: 'user-plus', desactivado: soloLectura, onClick: () => { setApuntar({ evento: null }); } }] : [];
    useCabecera({ ayuda: [{ icono: 'users', titulo: 'Tu portal', texto: 'Aquí ves solo lo tuyo: tus listas, tus invitados, tus ventas y tus comisiones.' }], acciones });

    if (!permisos.puede(PERMISOS_PORTAL.listas)) {
        return <EstadoSinPermiso seccion="Listas y RRPP" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }

    const porLista = new Map<string, string>();
    for (const ls of Object.values(listas.datos ?? {})) {
        for (const l of ls) {
            porLista.set(l.id, l.name ?? '');
        }
    }
    const datos: DatosPortal = {
        ops,
        eventos: listaEventos,
        listas: listas.datos ?? {},
        enlaces: enlaces.datos?.data ?? [],
        recargarEnlaces: enlaces.recargar,
        nombreEvento: (id) => listaEventos.find((e) => e.id === id)?.name ?? 'Evento',
        nombreLista: (id) => porLista.get(id) ?? 'Lista',
    };

    const ir = (s: Seccion, extra = '') => {
        navegar(`${ruta}?seccion=${s}${extra}`);
    };
    const indice = SECCIONES.findIndex((s) => s.valor === seccion);
    const titulo = SECCIONES[indice]?.etiqueta ?? 'Inicio';

    const copiarTexto = (texto: string) => {
        copiar(texto)
            .then(() => { avisar({ tipo: 'ok', texto: 'Enlace copiado' }); })
            .catch(() => { avisar({ tipo: 'error', texto: 'No se ha podido copiar. Mantén pulsado el enlace para copiarlo.' }); });
    };

    return (
        <div className="rv-rp">
            <div className="rv-rp__barra">
                <Segmentos
                    etiqueta="Portal de RRPP"
                    movil={movil}
                    items={SECCIONES.map((s) => ({ etiqueta: s.etiqueta, corta: s.corta, icono: s.icono }))}
                    valor={Math.max(indice, 0)}
                    onCambio={(i) => {
                        const s = SECCIONES[i];
                        if (s !== undefined) {
                            ir(s.valor);
                        }
                    }}
                />
            </div>
            <header className="rv-rp__titulo">
                <h2>{titulo}</h2>
                <span>{SUBTITULOS[seccion]}</span>
            </header>
            {seccion === 'inicio' && <Inicio props={props} datos={datos} cargando={eventos.datos === null && eventos.error === null} nombre={(yo.account.display_name || '').split(' ')[0] ?? ''} ir={ir} onApuntar={puedeApuntar && !soloLectura ? () => { setApuntar({ evento: null }); } : null} onCopiar={copiarTexto} />}
            {seccion === 'eventos' && <Eventos props={props} datos={datos} cargando={eventos.datos === null && eventos.error === null} onApuntar={puedeApuntar && !soloLectura ? (ev) => { setApuntar({ evento: ev }); } : null} onCopiar={copiarTexto} />}
            {seccion === 'invitados' && <Invitados props={props} datos={datos} onCopiar={copiarTexto} />}
            {(seccion === 'ventas' || seccion === 'comisiones') && <Ganancias props={props} datos={datos} seccion={seccion} />}
            {apuntar !== null && (
                <Apuntar
                    props={props}
                    datos={datos}
                    evento={apuntar.evento}
                    onCerrar={() => { setApuntar(null); }}
                    onApuntado={(id) => {
                        setApuntar(null);
                        listas.recargar();
                        navegar(`${ruta}?seccion=invitados&invitado=${encodeURIComponent(id)}`);
                    }}
                    onCopiar={copiarTexto}
                />
            )}
            {eventos.error !== null && seccion !== 'ventas' && seccion !== 'comisiones' && <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="circle-alert" /><span>{avisoBreveDeError(eventos.error) ?? 'No hemos podido cargar tus eventos.'}</span></p>}
        </div>
    );
}

interface PropsSeccion {
    props: PropsPantalla;
    datos: DatosPortal;
    cargando: boolean;
    onCopiar: (texto: string) => void;
}

function Inicio({ props, datos, cargando, nombre, ir, onApuntar, onCopiar }: PropsSeccion & { nombre: string; ir: (s: Seccion, extra?: string) => void; onApuntar: (() => void) | null }) {
    const { ops, negocio, navegar, ruta, destinos = [], recargarAvisos } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisos = useCarga(`avisos|${negocio}`, () => ops.listMyNotices(negocio, { state: 'unread', limit: 5 }));
    const proximo = datos.eventos[0];
    const apuntados = proximo === undefined ? 0 : (datos.listas[proximo.id] ?? []).reduce((n, l) => n + (l.my_quota?.used ?? 0), 0);
    const enlace = datos.enlaces.find((e) => e.kind === 'fixed' && e.status === 'active') ?? datos.enlaces.find((e) => e.status === 'active');
    const url = enlace?.instagram_url ?? enlace?.web_form_url ?? null;

    const verAviso = (tipo: string, id: string) => {
        if (tipo === 'invitado') {
            navegar(`${ruta}?seccion=invitados&invitado=${encodeURIComponent(id)}`);
            return;
        }
        const destino = enlaceDeReferencia(destinos, tipo, id);
        if (destino !== null) {
            navegar(destino.ruta);
        }
    };
    const leer = (id: string | null) => {
        (id === null ? ops.markAllNoticesRead(negocio) : ops.markNoticeRead(negocio, id))
            .then(() => {
                avisos.recargar();
                recargarAvisos?.();
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se han podido marcar como leídos.' }); });
    };

    const accesos: { texto: string; icono: Parameters<typeof Icono>[0]['nombre']; onClick: () => void; rojo?: boolean }[] = [];
    if (onApuntar !== null) {
        accesos.push({ texto: 'Apuntar a un invitado', icono: 'user-plus', onClick: onApuntar, rojo: true });
    }
    if (url !== null) {
        accesos.push({ texto: 'Copiar mi enlace', icono: 'copy', onClick: () => { onCopiar(url); } });
    }
    accesos.push({ texto: 'Ver invitados', icono: 'users', onClick: () => { ir('invitados'); } });
    if (permisos.puede(PERMISOS_PORTAL.comisiones)) {
        accesos.push({ texto: 'Mis comisiones', icono: 'percent', onClick: () => { ir('comisiones'); } });
        accesos.push({ texto: 'Mis ventas', icono: 'ticket', onClick: () => { ir('ventas'); } });
    }
    if (permisos.puede(PERMISOS_PORTAL.liquidaciones)) {
        accesos.push({ texto: 'Liquidaciones', icono: 'wallet', onClick: () => { ir('comisiones', '&vista=liquidaciones'); } });
    }

    const lista = avisos.datos?.data ?? [];
    return (
        <div className="rv-rp__inicio">
            {cargando ? (
                <div className="rv-rp-esqueleto" aria-busy="true" />
            ) : proximo === undefined ? (
                <p className="rv-rp__saludo">{nombre === '' ? 'Ahora mismo no tienes eventos con listas abiertas.' : `${nombre}, ahora mismo no tienes eventos con listas abiertas.`}</p>
            ) : (
                <div>
                    <p className="rv-rp__saludo">
                        {nombre === '' ? 'Llevas ' : `${nombre}, llevas `}
                        <em>{`${String(apuntados)} ${apuntados === 1 ? 'apuntado' : 'apuntados'}`}</em>.
                    </p>
                    <p className="rv-rp__sub">{`En tus listas de ${proximo.name ?? ''}, ${fechaEvento(proximo.starts_at ?? '', proximo.timezone)}.`}</p>
                </div>
            )}
            <section aria-label="Tus accesos" className="rv-rp-accesos">
                {accesos.map((a) => (
                    <button key={a.texto} type="button" className={`rv-rp-acceso${a.rojo === true ? ' rv-rp-acceso--rojo' : ''}`} onClick={a.onClick}>
                        <span aria-hidden="true"><Icono nombre={a.icono} /></span>
                        <strong>{a.texto}</strong>
                    </button>
                ))}
            </section>
            {lista.length > 0 && (
                <section className="rv-ca-lista" aria-label="Avisos">
                    {lista.map((a) => (
                        <div key={a.id} className="rv-ca-fila-caja">
                            <div className="rv-ca-fila">
                                <span className="rv-ca-baldosa"><Icono nombre={a.severity === 'critical' ? 'triangle-alert' : 'bell'} /></span>
                                <span className="rv-ca-fila__texto">
                                    <strong>{a.title ?? 'Aviso'}</strong>
                                    {a.body != null && <span>{a.body}</span>}
                                </span>
                                {a.link != null && (
                                    <Boton tipo="secundario" onClick={() => { const l = a.link; if (l != null) { leer(a.id); verAviso(l.type, l.id); } }}>
                                        Ver
                                    </Boton>
                                )}
                            </div>
                        </div>
                    ))}
                    <div className="rv-ca-deshacer">
                        <Boton tipo="fantasma" icono="check" onClick={() => { leer(null); }}>Marcar como leídos</Boton>
                    </div>
                </section>
            )}
        </div>
    );
}

function Eventos({ props, datos, cargando, onApuntar, onCopiar }: PropsSeccion & { onApuntar: ((evento: string) => void) | null }) {
    const { negocio, soloLectura, membresia, movil } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const gestionar = permisos.puede(PERMISOS_PORTAL.gestionarEnlaces) && !soloLectura;
    const [ocupado, setOcupado] = useState<string | null>(null);

    const hacer = (clave: string, accion: () => Promise<unknown>, ok: string) => {
        setOcupado(clave);
        accion()
            .then(() => {
                avisar({ tipo: 'ok', texto: ok });
                datos.recargarEnlaces();
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido hacer. Prueba de nuevo.' }); })
            .finally(() => { setOcupado(null); });
    };

    if (cargando) {
        return <div className="rv-rp-esqueleto" aria-busy="true" />;
    }
    const fijo = datos.enlaces.find((e) => e.kind === 'fixed');
    return (
        <div className="rv-ca-apartado">
            {datos.eventos.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa"><Icono nombre="calendar" /></span>
                    <strong>No tienes eventos con listas</strong>
                    <span>Cuando el negocio te asigne a una lista, la verás aquí con tu cupo y tus enlaces.</span>
                </div>
            ) : (
                <div className="rv-ca-lista">
                    {datos.eventos.map((ev) => {
                        const ls = datos.listas[ev.id] ?? [];
                        const enlace = datos.enlaces.find((e) => e.kind === 'event' && e.event_id === ev.id);
                        return (
                            <div key={ev.id} className="rv-ca-fila-caja rv-rp-evento">
                                <div className="rv-rp-evento__cab">
                                    <strong>{ev.name}</strong>
                                    <span>{fechaEvento(ev.starts_at ?? '', ev.timezone)}</span>
                                </div>
                                {ls.map((l) => (
                                    <div key={l.id} className="rv-rp-cupo">
                                        <span>{l.name}{l.status !== 'open' && <small>{l.status === 'closed' ? ' · Cerrada' : ' · Cancelada'}</small>}</span>
                                        <span>{l.my_quota == null ? '' : l.my_quota.quota == null ? `${String(l.my_quota.used)} apuntados` : `${String(l.my_quota.used)} de ${String(l.my_quota.quota)}`}</span>
                                    </div>
                                ))}
                                {enlace !== undefined && <Enlace enlace={enlace} gestionar={gestionar} ocupado={ocupado} onCopiar={onCopiar} hacer={hacer} ops={datos.ops} negocio={negocio} />}
                                {onApuntar !== null && ls.some((l) => l.status === 'open') && (
                                    <Boton tipo="secundario" icono="user-plus" movil={movil} onClick={() => { onApuntar(ev.id); }}>Apuntar a un invitado</Boton>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            <div className="rv-ca-lista">
                <div className="rv-ca-fila-caja rv-rp-evento">
                    <div className="rv-rp-evento__cab">
                        <strong>Tu enlace fijo</strong>
                        <span>Opcional: uno solo para todos tus eventos, para dejarlo en tu perfil.</span>
                    </div>
                    {fijo !== undefined ? (
                        <Enlace enlace={fijo} gestionar={gestionar} ocupado={ocupado} onCopiar={onCopiar} hacer={hacer} ops={datos.ops} negocio={negocio} />
                    ) : gestionar && membresia !== undefined ? (
                        <Boton tipo="secundario" icono="plus" ocupado={ocupado === 'fijo'} onClick={() => { hacer('fijo', () => datos.ops.createPromoterFixedLink(negocio, membresia.member_id), 'Enlace fijo creado'); }}>
                            Crear mi enlace fijo
                        </Boton>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function Enlace({ enlace, gestionar, ocupado, onCopiar, hacer, ops, negocio }: { enlace: EnlaceRrpp; gestionar: boolean; ocupado: string | null; onCopiar: (t: string) => void; hacer: (clave: string, accion: () => Promise<unknown>, ok: string) => void; ops: OpsPortal; negocio: string }) {
    const motivos = enlace.unavailable_reasons ?? [];
    return (
        <div className="rv-rp-enlaces">
            {enlace.instagram_url != null ? (
                <div className="rv-rp-enlace">
                    <Icono nombre="instagram" />
                    <code>{sinProtocolo(enlace.instagram_url)}</code>
                    <Boton tipo="secundario" icono="copy" onClick={() => { onCopiar(enlace.instagram_url ?? ''); }}>Copiar enlace</Boton>
                </div>
            ) : motivos.includes('instagram_not_connected') ? (
                <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="instagram" /><span>Instagram sin conectar: el negocio aún no ha conectado su Instagram, así que no hay enlace de Instagram.</span></p>
            ) : null}
            {enlace.web_form_url != null ? (
                <div className="rv-rp-enlace">
                    <Icono nombre="globe" />
                    <code>{sinProtocolo(enlace.web_form_url)}</code>
                    <Boton tipo="secundario" icono="copy" onClick={() => { onCopiar(enlace.web_form_url ?? ''); }}>Copiar</Boton>
                </div>
            ) : motivos.includes('site_not_published') ? (
                <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="globe" /><span>Sin formulario web: el negocio aún no ha publicado su web.</span></p>
            ) : null}
            {enlace.web_form_delivery === 'whatsapp' && <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="message-circle" /><span>Quien se apunte en tu formulario recibe el pase por WhatsApp.</span></p>}
            {enlace.status === 'deactivated' && <p className="rv-ca-nota"><Icono nombre="circle-alert" /><span>Este enlace está desactivado. Regenera uno nuevo para volver a usarlo.</span></p>}
            {gestionar && (
                <div className="rv-rp-enlace__acciones">
                    <Boton tipo="fantasma" icono="refresh-cw" ocupado={ocupado === `r${enlace.id}`} onClick={() => { hacer(`r${enlace.id}`, () => ops.regeneratePromoterLink(negocio, enlace.id), 'Enlace nuevo listo. El anterior ya no vale.'); }}>
                        Regenerar
                    </Boton>
                    {enlace.status === 'active' && (
                        <Boton tipo="fantasma" icono="x" ocupado={ocupado === `d${enlace.id}`} onClick={() => { hacer(`d${enlace.id}`, () => ops.deactivatePromoterLink(negocio, enlace.id), 'Enlace desactivado'); }}>
                            Desactivar
                        </Boton>
                    )}
                </div>
            )}
        </div>
    );
}
